import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { CheckIcon, ChevronDown, XIcon, WandSparkles, PlusCircle } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';

/**
 * Animation types and configurations
 */
export interface AnimationConfig {
  /** Badge animation type */
  badgeAnimation?: 'bounce' | 'pulse' | 'wiggle' | 'fade' | 'slide' | 'none';
  /** Popover animation type */
  popoverAnimation?: 'scale' | 'slide' | 'fade' | 'flip' | 'none';
  /** Option hover animation type */
  optionHoverAnimation?: 'highlight' | 'scale' | 'glow' | 'none';
  /** Animation duration in seconds */
  duration?: number;
  /** Animation delay in seconds */
  delay?: number;
}

/**
 * Variants for the multi-select component to handle different styles.
 * Uses class-variance-authority (cva) to define different styles based on "variant" prop.
 */
const multiSelectVariants = cva('m-1 transition-all duration-300 ease-in-out', {
  variants: {
    variant: {
      default: 'border-foreground/10 text-foreground bg-card hover:bg-card/80',
      secondary: 'border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80',
      destructive: 'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
      inverted: 'inverted'
    },
    badgeAnimation: {
      bounce: 'hover:-translate-y-1 hover:scale-110',
      pulse: 'hover:animate-pulse',
      wiggle: 'hover:animate-wiggle',
      fade: 'hover:opacity-80',
      slide: 'hover:translate-x-1',
      none: ''
    }
  },
  defaultVariants: {
    variant: 'default',
    badgeAnimation: 'bounce'
  }
});

/**
 * Option interface for MultiSelect component
 */
interface MultiSelectOption {
  /** The text to display for the option. */
  label: string;
  /** The unique value associated with the option. */
  value: string;
  /** Optional icon component to display alongside the option. */
  icon?: React.ComponentType<{ className?: string }>;
  /** Whether this option is disabled */
  disabled?: boolean;
  /** Optional status badge rendered next to the label (e.g. persona validation status) */
  badge?: { label: string; className?: string };
  /** Custom styling for the option */
  style?: {
    /** Colour shown as a dot before the option's label and its selected chip */
    badgeColor?: string;
    /** Custom icon color */
    iconColor?: string;
    /** Gradient background for badge */
    gradient?: string;
  };
}

/**
 * Group interface for organizing options
 */
interface MultiSelectGroup {
  /** Group heading */
  heading: string;
  /** Options in this group */
  options: MultiSelectOption[];
}

/**
 * Props for MultiSelect component
 */
interface MultiSelectProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'animationConfig'>, VariantProps<typeof multiSelectVariants> {
  /**
   * An array of option objects or groups to be displayed in the multi-select component.
   */
  options: MultiSelectOption[] | MultiSelectGroup[];
  /**
   * Placeholder text to be displayed when no values are selected.
   * Optional, defaults to "Search options...".
   */
  popoverPlaceholder?: string;
  /**
   * Callback function triggered when the selected values change.
   * Receives an array of the new selected values.
   */
  onValueChange: (value: string[]) => void;

  /** The default selected values when the component mounts. */
  defaultValue?: string[];

  /**
   * Placeholder text to be displayed when no values are selected.
   * Optional, defaults to "Select options".
   */
  placeholder?: string;

  /**
   * Animation duration in seconds for the visual effects (e.g., bouncing badges).
   * Optional, defaults to 0 (no animation).
   */
  animation?: number;

  /**
   * Advanced animation configuration for different component parts.
   * Optional, allows fine-tuning of various animation effects.
   */
  animationConfig?: AnimationConfig;

  /**
   * Maximum number of items to display. Extra selected items will be summarized.
   * Optional, defaults to 3.
   */
  maxCount?: number;

  /**
   * The modality of the popover. When set to true, interaction with outside elements
   * will be disabled and only popover content will be visible to screen readers.
   * Optional, defaults to false.
   */
  modalPopover?: boolean;

  /**
   * If true, renders the multi-select component as a child of another component.
   * Optional, defaults to false.
   */
  asChild?: boolean;

  /**
   * Additional class names to apply custom styles to the multi-select component.
   * Optional, can be used to add custom styles.
   */
  className?: string;

  /**
   * If true, disables the select all functionality.
   * Optional, defaults to false.
   */
  hideSelectAll?: boolean;

  /**
   * If true, shows search functionality in the popover.
   * If false, hides the search input completely.
   * Optional, defaults to true.
   */
  searchable?: boolean;

  /**
   * Custom empty state message when no options match search.
   * Optional, defaults to "No results found."
   */
  emptyIndicator?: React.ReactNode;

  /**
   * If true, allows the component to grow and shrink with its content.
   * If false, uses fixed width behavior.
   * Optional, defaults to false.
   */
  autoSize?: boolean;

  /**
   * If true, shows badges in a single line with horizontal scroll.
   * If false, badges wrap to multiple lines.
   * Optional, defaults to false.
   */
  singleLine?: boolean;

  /**
   * Custom CSS class for the popover content.
   * Optional, can be used to customize popover appearance.
   */
  popoverClassName?: string;

  /**
   * If true, disables the component completely.
   * Optional, defaults to false.
   */
  disabled?: boolean;

  /**
   * Responsive configuration for different screen sizes.
   * Allows customizing maxCount and other properties based on viewport.
   * Can be boolean true for default responsive behavior or an object for custom configuration.
   */
  responsive?:
    | boolean
    | {
        /** Configuration for mobile devices (< 640px) */
        mobile?: {
          maxCount?: number;
          hideIcons?: boolean;
          compactMode?: boolean;
        };
        /** Configuration for tablet devices (640px - 1024px) */
        tablet?: {
          maxCount?: number;
          hideIcons?: boolean;
          compactMode?: boolean;
        };
        /** Configuration for desktop devices (> 1024px) */
        desktop?: {
          maxCount?: number;
          hideIcons?: boolean;
          compactMode?: boolean;
        };
      };

  /**
   * Minimum width for the component.
   * Optional, defaults to auto-sizing based on content.
   * When set, component will not shrink below this width.
   */
  minWidth?: string;

  /**
   * Maximum width for the component.
   * Optional, defaults to 100% of container.
   * Component will not exceed container boundaries.
   */
  maxWidth?: string;

  /**
   * If true, automatically removes duplicate options based on their value.
   * Optional, defaults to false (shows warning in dev mode instead).
   */
  deduplicateOptions?: boolean;

  /**
   * If true, the component will reset its internal state when defaultValue changes.
   * Useful for React Hook Form integration and form reset functionality.
   * Optional, defaults to true.
   */
  resetOnDefaultValueChange?: boolean;

  /**
   * If true, automatically closes the popover after selecting an option.
   * Useful for single-selection-like behavior or mobile UX.
   * Optional, defaults to false.
   */
  closeOnSelect?: boolean;

  /**
   * If true, allows users to create new options by typing values that don't exist.
   * Shows a "Create [value]" option when the search term doesn't match any existing option.
   * Optional, defaults to false.
   */
  creatable?: boolean;

  /**
   * Callback function triggered when a new option is created (only works with creatable=true).
   * Receives the newly created option value.
   * Optional.
   */
  onCreateOption?: (value: string) => void;

  /**
   * If true, renders every selected value as its own removable chip (wrapping as
   * needed) instead of the default "first value + N more" summary. Ignores
   * `maxCount`/responsive truncation — all selections are always visible.
   * Optional, defaults to false.
   */
  showAllSelected?: boolean;
}

/**
 * Imperative methods exposed through ref
 */
export interface MultiSelectRef {
  /**
   * Programmatically reset the component to its default value
   */
  reset: () => void;
  /**
   * Get current selected values
   */
  getSelectedValues: () => string[];
  /**
   * Set selected values programmatically
   */
  setSelectedValues: (values: string[]) => void;
  /**
   * Clear all selected values
   */
  clear: () => void;
  /**
   * Focus the component
   */
  focus: () => void;
}

/** An option's own colour as a small dot; renders nothing when the option has none. */
function OptionColorDot({ color }: { color?: string }) {
  if (!color) return null;
  return <span aria-hidden='true' className='mr-1.5 inline-block size-2 shrink-0 rounded-full' style={{ backgroundColor: color }} />;
}

export const MultiSelect = React.forwardRef<MultiSelectRef, MultiSelectProps>(
  (
    {
      options,
      onValueChange,
      variant,
      defaultValue = [],
      placeholder = 'Select options',
      popoverPlaceholder = 'Search options...',
      animation = 0,
      animationConfig,
      maxCount = 3,
      modalPopover = false,
      asChild = false,
      className,
      hideSelectAll = false,
      searchable = true,
      emptyIndicator,
      autoSize = false,
      singleLine = false,
      popoverClassName,
      disabled = false,
      responsive,
      minWidth,
      maxWidth,
      deduplicateOptions = false,
      resetOnDefaultValueChange = true,
      closeOnSelect = false,
      creatable = false,
      onCreateOption,
      showAllSelected = false,
      ...props
    },
    ref
  ) => {
    const [selectedValues, setSelectedValues] = React.useState<string[]>(defaultValue);
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
    const [isAnimating, setIsAnimating] = React.useState(false);
    const [searchValue, setSearchValue] = React.useState('');
    // cmdk auto-highlights the first item as soon as the list mounts, which
    // reads as a hover state nobody's cursor produced — keep it controlled
    // and start empty so nothing is highlighted until the mouse/keyboard
    // actually moves onto an item.
    const [highlightedValue, setHighlightedValue] = React.useState('');

    const [politeMessage, setPoliteMessage] = React.useState('');
    const [assertiveMessage, setAssertiveMessage] = React.useState('');
    const prevSelectedCount = React.useRef(selectedValues.length);
    const prevIsOpen = React.useRef(isPopoverOpen);
    const prevSearchValue = React.useRef(searchValue);

    const announce = React.useCallback((message: string, priority: 'polite' | 'assertive' = 'polite') => {
      if (priority === 'assertive') {
        setAssertiveMessage(message);
        setTimeout(() => setAssertiveMessage(''), 100);
      } else {
        setPoliteMessage(message);
        setTimeout(() => setPoliteMessage(''), 100);
      }
    }, []);

    const multiSelectId = React.useId();
    const listboxId = `${multiSelectId}-listbox`;
    const triggerDescriptionId = `${multiSelectId}-description`;
    const selectedCountId = `${multiSelectId}-count`;

    const prevDefaultValueRef = React.useRef<string[]>(defaultValue);

    const isGroupedOptions = React.useCallback((opts: MultiSelectOption[] | MultiSelectGroup[]): opts is MultiSelectGroup[] => {
      return opts.length > 0 && 'heading' in opts[0];
    }, []);

    const arraysEqual = React.useCallback((a: string[], b: string[]): boolean => {
      if (a.length !== b.length) return false;
      const sortedA = [...a].sort();
      const sortedB = [...b].sort();
      return sortedA.every((val, index) => val === sortedB[index]);
    }, []);

    const resetToDefault = React.useCallback(() => {
      setSelectedValues(defaultValue);
      setIsPopoverOpen(false);
      setSearchValue('');
      onValueChange(defaultValue);
    }, [defaultValue, onValueChange]);

    const buttonRef = React.useRef<HTMLButtonElement>(null);

    React.useImperativeHandle(
      ref,
      () => ({
        reset: resetToDefault,
        getSelectedValues: () => selectedValues,
        setSelectedValues: (values: string[]) => {
          setSelectedValues(values);
          onValueChange(values);
        },
        clear: () => {
          setSelectedValues([]);
          onValueChange([]);
        },
        focus: () => {
          if (buttonRef.current) {
            buttonRef.current.focus();
            const originalOutline = buttonRef.current.style.outline;
            const originalOutlineOffset = buttonRef.current.style.outlineOffset;
            buttonRef.current.style.outline = '2px solid hsl(var(--ring))';
            buttonRef.current.style.outlineOffset = '2px';
            setTimeout(() => {
              if (buttonRef.current) {
                buttonRef.current.style.outline = originalOutline;
                buttonRef.current.style.outlineOffset = originalOutlineOffset;
              }
            }, 1000);
          }
        }
      }),
      [resetToDefault, selectedValues, onValueChange]
    );

    const [screenSize, setScreenSize] = React.useState<'mobile' | 'tablet' | 'desktop'>('desktop');

    React.useEffect(() => {
      if (typeof window === 'undefined') return;
      const handleResize = () => {
        const width = window.innerWidth;
        if (width < 640) {
          setScreenSize('mobile');
        } else if (width < 1024) {
          setScreenSize('tablet');
        } else {
          setScreenSize('desktop');
        }
      };
      handleResize();
      window.addEventListener('resize', handleResize);
      return () => {
        if (typeof window !== 'undefined') {
          window.removeEventListener('resize', handleResize);
        }
      };
    }, []);

    const getResponsiveSettings = () => {
      if (!responsive) {
        return {
          maxCount: maxCount,
          hideIcons: false,
          compactMode: false
        };
      }
      if (responsive === true) {
        const defaultResponsive = {
          mobile: { maxCount: 2, hideIcons: false, compactMode: true },
          tablet: { maxCount: 4, hideIcons: false, compactMode: false },
          desktop: { maxCount: 6, hideIcons: false, compactMode: false }
        };
        const currentSettings = defaultResponsive[screenSize];
        return {
          maxCount: currentSettings?.maxCount ?? maxCount,
          hideIcons: currentSettings?.hideIcons ?? false,
          compactMode: currentSettings?.compactMode ?? false
        };
      }
      const currentSettings = responsive[screenSize];
      return {
        maxCount: currentSettings?.maxCount ?? maxCount,
        hideIcons: currentSettings?.hideIcons ?? false,
        compactMode: currentSettings?.compactMode ?? false
      };
    };

    const responsiveSettings = getResponsiveSettings();

    const getPopoverAnimationClass = () => {
      if (animationConfig?.popoverAnimation) {
        switch (animationConfig.popoverAnimation) {
          case 'scale':
            return 'animate-scaleIn';
          case 'slide':
            return 'animate-slideInDown';
          case 'fade':
            return 'animate-fadeIn';
          case 'flip':
            return 'animate-flipIn';
          case 'none':
            return '';
          default:
            return '';
        }
      }
      return '';
    };

    const getAllOptions = React.useCallback((): MultiSelectOption[] => {
      if (options.length === 0) return [];
      let allOptions: MultiSelectOption[];
      if (isGroupedOptions(options)) {
        allOptions = options.flatMap((group) => group.options);
      } else {
        allOptions = options;
      }
      const valueSet = new Set<string>();
      const duplicates: string[] = [];
      const uniqueOptions: MultiSelectOption[] = [];
      allOptions.forEach((option) => {
        if (valueSet.has(option.value)) {
          duplicates.push(option.value);
          if (!deduplicateOptions) {
            uniqueOptions.push(option);
          }
        } else {
          valueSet.add(option.value);
          uniqueOptions.push(option);
        }
      });
      if (import.meta.env.DEV && duplicates.length > 0) {
        const action = deduplicateOptions ? 'automatically removed' : 'detected';
        console.warn(
          `MultiSelect: Duplicate option values ${action}: ${duplicates.join(', ')}. ` +
            `${
              deduplicateOptions
                ? 'Duplicates have been removed automatically.'
                : "This may cause unexpected behavior. Consider setting 'deduplicateOptions={true}' or ensure all option values are unique."
            }`
        );
      }
      return deduplicateOptions ? uniqueOptions : allOptions;
    }, [options, deduplicateOptions, isGroupedOptions]);

    const getOptionByValue = React.useCallback(
      (value: string): MultiSelectOption | undefined => {
        const option = getAllOptions().find((option) => option.value === value);
        if (!option && import.meta.env.DEV) {
          console.warn(`MultiSelect: Option with value "${value}" not found in options list`);
        }
        return option;
      },
      [getAllOptions]
    );

    const filteredOptions = React.useMemo(() => {
      if (!searchable || !searchValue) return options;
      if (options.length === 0) return [];
      if (isGroupedOptions(options)) {
        return options
          .map((group) => ({
            ...group,
            options: group.options.filter(
              (option) =>
                option.label.toLowerCase().includes(searchValue.toLowerCase()) ||
                option.value.toLowerCase().includes(searchValue.toLowerCase())
            )
          }))
          .filter((group) => group.options.length > 0);
      }
      return options.filter(
        (option) =>
          option.label.toLowerCase().includes(searchValue.toLowerCase()) ||
          option.value.toLowerCase().includes(searchValue.toLowerCase())
      );
    }, [options, searchValue, searchable, isGroupedOptions]);

    const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        setIsPopoverOpen(true);
      } else if (event.key === 'Backspace' && !event.currentTarget.value) {
        const newSelectedValues = [...selectedValues];
        newSelectedValues.pop();
        setSelectedValues(newSelectedValues);
        onValueChange(newSelectedValues);
      }
    };

    const toggleOption = (optionValue: string) => {
      if (disabled) return;
      const option = getOptionByValue(optionValue);
      if (option?.disabled) return;
      const newSelectedValues = selectedValues.includes(optionValue)
        ? selectedValues.filter((value) => value !== optionValue)
        : [...selectedValues, optionValue];
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
      if (closeOnSelect) {
        setIsPopoverOpen(false);
      }
    };

    const handleClear = () => {
      if (disabled) return;
      setSelectedValues([]);
      onValueChange([]);
    };

    const handleTogglePopover = () => {
      if (disabled) return;
      setIsPopoverOpen((prev) => !prev);
    };

    const toggleAll = () => {
      if (disabled) return;
      const allOptions = getAllOptions().filter((option) => !option.disabled);
      if (selectedValues.length === allOptions.length) {
        handleClear();
      } else {
        const allValues = allOptions.map((option) => option.value);
        setSelectedValues(allValues);
        onValueChange(allValues);
      }

      if (closeOnSelect) {
        setIsPopoverOpen(false);
      }
    };

    // Check if search value can be created as a new option
    const canCreateOption = React.useMemo(() => {
      if (!creatable || !searchValue.trim()) return false;
      const trimmedValue = searchValue.trim().toLowerCase();
      const allOptions = getAllOptions();
      // Check if value already exists in options
      const existsInOptions = allOptions.some(
        (opt) => opt.value.toLowerCase() === trimmedValue || opt.label.toLowerCase() === trimmedValue
      );
      // Check if value is already selected
      const alreadySelected = selectedValues.some((val) => val.toLowerCase() === trimmedValue);
      return !existsInOptions && !alreadySelected;
    }, [creatable, searchValue, getAllOptions, selectedValues]);

    // Handle creating a new option
    const handleCreateOption = () => {
      if (!canCreateOption || disabled) return;
      const newValue = searchValue.trim();
      const newSelectedValues = [...selectedValues, newValue];
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
      onCreateOption?.(newValue);
      setSearchValue('');
      if (closeOnSelect) {
        setIsPopoverOpen(false);
      }
    };

    React.useEffect(() => {
      if (!resetOnDefaultValueChange) return;
      const prevDefaultValue = prevDefaultValueRef.current;
      if (!arraysEqual(prevDefaultValue, defaultValue)) {
        if (!arraysEqual(selectedValues, defaultValue)) {
          setSelectedValues(defaultValue);
        }
        prevDefaultValueRef.current = [...defaultValue];
      }
    }, [defaultValue, selectedValues, arraysEqual, resetOnDefaultValueChange]);

    const getWidthConstraints = () => {
      const defaultMinWidth = screenSize === 'mobile' ? '0px' : '200px';
      const effectiveMinWidth = minWidth || defaultMinWidth;
      const effectiveMaxWidth = maxWidth || '100%';
      return {
        minWidth: effectiveMinWidth,
        maxWidth: effectiveMaxWidth,
        width: autoSize ? 'auto' : '100%'
      };
    };

    const widthConstraints = getWidthConstraints();

    React.useEffect(() => {
      if (!isPopoverOpen) {
        setSearchValue('');
        setHighlightedValue('');
      }
    }, [isPopoverOpen]);

    React.useEffect(() => {
      const selectedCount = selectedValues.length;
      const allOptions = getAllOptions();
      const totalOptions = allOptions.filter((opt) => !opt.disabled).length;
      if (selectedCount !== prevSelectedCount.current) {
        const diff = selectedCount - prevSelectedCount.current;
        if (diff > 0) {
          const addedItems = selectedValues.slice(-diff);
          const addedLabels = addedItems.map((value) => allOptions.find((opt) => opt.value === value)?.label).filter(Boolean);

          if (addedLabels.length === 1) {
            announce(`${addedLabels[0]} selected. ${selectedCount} of ${totalOptions} options selected.`);
          } else {
            announce(`${addedLabels.length} options selected. ${selectedCount} of ${totalOptions} total selected.`);
          }
        } else if (diff < 0) {
          announce(`Option removed. ${selectedCount} of ${totalOptions} options selected.`);
        }
        prevSelectedCount.current = selectedCount;
      }

      if (isPopoverOpen !== prevIsOpen.current) {
        if (isPopoverOpen) {
          announce(`Dropdown opened. ${totalOptions} options available. Use arrow keys to navigate.`);
        } else {
          announce('Dropdown closed.');
        }
        prevIsOpen.current = isPopoverOpen;
      }

      if (searchValue !== prevSearchValue.current && searchValue !== undefined) {
        if (searchValue && isPopoverOpen) {
          const filteredCount = allOptions.filter(
            (opt) =>
              opt.label.toLowerCase().includes(searchValue.toLowerCase()) ||
              opt.value.toLowerCase().includes(searchValue.toLowerCase())
          ).length;

          announce(`${filteredCount} option${filteredCount === 1 ? '' : 's'} found for "${searchValue}"`);
        }
        prevSearchValue.current = searchValue;
      }
    }, [selectedValues, isPopoverOpen, searchValue, announce, getAllOptions]);

    return (
      <>
        <div className='sr-only'>
          <div aria-live='polite' aria-atomic='true' role='status'>
            {politeMessage}
          </div>
          <div aria-live='assertive' aria-atomic='true' role='alert'>
            {assertiveMessage}
          </div>
        </div>

        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen} modal={modalPopover}>
          <div id={triggerDescriptionId} className='sr-only'>
            Multi-select dropdown. Use arrow keys to navigate, Enter to select, and Escape to close.
          </div>
          <div id={selectedCountId} className='sr-only' aria-live='polite'>
            {selectedValues.length === 0
              ? 'No options selected'
              : `${selectedValues.length} option${selectedValues.length === 1 ? '' : 's'} selected: ${selectedValues
                  .map((value) => getOptionByValue(value)?.label)
                  .filter(Boolean)
                  .join(', ')}`}
          </div>

          <PopoverTrigger asChild>
            <Button
              ref={buttonRef}
              {...props}
              onClick={handleTogglePopover}
              disabled={disabled}
              role='combobox'
              aria-expanded={isPopoverOpen}
              aria-haspopup='listbox'
              aria-controls={isPopoverOpen ? listboxId : undefined}
              aria-describedby={`${triggerDescriptionId} ${selectedCountId}`}
              aria-label={`Multi-select: ${selectedValues.length} of ${getAllOptions().length} options selected. ${placeholder}`}
              className={cn(
                'flex p-1 rounded-[12px] border border-border min-h-10 h-auto items-center justify-between bg-white hover:bg-white [&_svg]:pointer-events-auto',
                autoSize ? 'w-auto' : 'w-full',
                responsiveSettings.compactMode && 'min-h-8 text-xs',
                screenSize === 'mobile' && 'min-h-12 text-base',
                disabled && 'opacity-50 cursor-not-allowed',
                className
              )}
              style={{
                ...widthConstraints,
                maxWidth: `min(${widthConstraints.maxWidth}, 100%)`
              }}
            >
              {showAllSelected && selectedValues.length > 0 ? (
                <div className='flex items-center justify-between w-full gap-2'>
                  <div className='flex flex-wrap items-center gap-1.5 flex-1 min-w-0 py-1'>
                    {selectedValues.map((value) => (
                      <span
                        key={value}
                        /* 12px, the same as the placeholder below and every other field label in
                           the product. The chip was 11px while the placeholder inherited the
                           trigger's 14px, so the field changed size the moment you picked
                           something. */
                        className='inline-flex items-center gap-1 rounded-full border border-gray-500 bg-white px-2 py-1 text-xs text-text-emphasis'
                      >
                        <OptionColorDot color={getOptionByValue(value)?.style?.badgeColor} />
                        {getOptionByValue(value)?.label || value}
                        <button
                          type='button'
                          onClick={(event) => {
                            event.stopPropagation();
                            toggleOption(value);
                          }}
                          aria-label={`Remove ${getOptionByValue(value)?.label || value}`}
                          className='flex items-center justify-center text-muted-foreground hover:text-foreground focus:outline-none'
                        >
                          <XIcon className='h-3 w-3' />
                        </button>
                      </span>
                    ))}
                  </div>
                  <ChevronDown className='h-4 shrink-0 cursor-pointer text-muted-foreground' aria-hidden='true' />
                </div>
              ) : selectedValues.length > 0 ? (
                <div className='flex items-center justify-between w-full overflow-hidden'>
                  <span className='truncate mx-3 text-xs text-muted-foreground'>
                    <OptionColorDot color={getOptionByValue(selectedValues[0])?.style?.badgeColor} />
                    {getOptionByValue(selectedValues[0])?.label || selectedValues[0]}
                  </span>
                  <div className='flex items-center shrink-0'>
                    <div
                      role='button'
                      tabIndex={0}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleClear();
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          event.stopPropagation();
                          handleClear();
                        }
                      }}
                      aria-label={`Clear all ${selectedValues.length} selected options`}
                      className='flex items-center justify-center cursor-pointer text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 rounded-sm'
                    >
                      <XIcon className='h-4 w-4' />
                    </div>
                    {selectedValues.length > 1 && (
                      <>
                        <Separator orientation='vertical' className='mx-2 flex min-h-6 h-full' />
                        <span className='text-muted-foreground text-sm'>
                          +{selectedValues.length - 1}
                        </span>
                      </>
                    )}
                    <Separator orientation='vertical' className='mx-2 flex min-h-6 h-full' />
                    <ChevronDown className='h-4 cursor-pointer text-muted-foreground' aria-hidden='true' />
                  </div>
                </div>
              ) : (
                <div className='flex items-center justify-between w-full mx-auto'>
                  <span className='text-muted-foreground mx-3 text-xs'>{placeholder}</span>
                  <ChevronDown className='h-4 cursor-pointer text-muted-foreground mx-2' />
                </div>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            id={listboxId}
            role='listbox'
            aria-multiselectable='true'
            aria-label='Available options'
            className={cn(
              'w-[var(--radix-popover-trigger-width)] p-0 bg-white/80 backdrop-blur-[6px] border-border/60 shadow-[0px_3px_12px_0px_rgba(0,0,0,0.08)] rounded-2xl overflow-hidden',
              getPopoverAnimationClass(),
              popoverClassName
            )}
            style={{
              animationDuration: `${animationConfig?.duration || animation}s`,
              animationDelay: `${animationConfig?.delay || 0}s`,
              touchAction: 'manipulation'
            }}
            align='start'
            side='bottom'
            avoidCollisions={false}
            collisionPadding={8}
            onEscapeKeyDown={() => setIsPopoverOpen(false)}
          >
            {/* `Command`'s own `bg-popover` is opaque and would paint over PopoverContent's
                translucent `bg-white/80 backdrop-blur`, hiding it entirely — override to
                transparent so the popover's own background actually shows through. */}
            <Command
              shouldFilter={false}
              className='bg-transparent rounded-2xl'
              value={highlightedValue}
              onValueChange={setHighlightedValue}
            >
              {searchable && (
                <CommandInput
                  placeholder={popoverPlaceholder}
                  onKeyDown={handleInputKeyDown}
                  value={searchValue}
                  onValueChange={setSearchValue}
                  aria-label='Search through available options'
                  aria-describedby={`${multiSelectId}-search-help`}
                  wrapperClassName='border-b-0 h-8 gap-2.5 px-2.5 mx-1.5 mt-1.5 rounded-[11px] bg-gray-100'
                  iconClassName='size-3.5 opacity-100 text-gray-800'
                  className='text-xs placeholder:text-gray-800 h-auto py-0'
                />
              )}
              {searchable && (
                <div id={`${multiSelectId}-search-help`} className='sr-only'>
                  Type to filter options. Use arrow keys to navigate results.
                </div>
              )}
              <CommandList
                className={cn(
                  'max-h-[min(240px,calc(var(--radix-popover-content-available-height)-44px))] overflow-y-auto multiselect-scrollbar',
                  'overscroll-behavior-y-contain'
                )}
                // When this popover opens from inside a modal Dialog, Radix's scroll-lock on
                // <body> (react-remove-scroll) swallows native wheel-to-scroll on this
                // separately-portaled list — it's genuinely scrollable (scrollTop works),
                // the browser just never gets to apply the wheel delta. Do it by hand.
                onWheel={(e) => {
                  e.currentTarget.scrollTop += e.deltaY;
                }}
              >
                {!canCreateOption && <CommandEmpty>{emptyIndicator || 'No results found.'}</CommandEmpty>}
                {canCreateOption && (
                  <CommandGroup forceMount>
                    <CommandItem
                      key='create-option'
                      value={searchValue.trim()}
                      onSelect={handleCreateOption}
                      role='option'
                      aria-label={`Create new option: ${searchValue.trim()}`}
                      className='cursor-pointer'
                      forceMount
                    >
                      <PlusCircle className='mr-2 h-4 w-4 text-muted-foreground' aria-hidden='true' />
                      <span>Create "{searchValue.trim()}"</span>
                    </CommandItem>
                  </CommandGroup>
                )}
                {!hideSelectAll && !searchValue && (
                  <CommandGroup>
                    <CommandItem
                      key='all'
                      onSelect={toggleAll}
                      role='option'
                      aria-selected={selectedValues.length === getAllOptions().filter((opt) => !opt.disabled).length}
                      aria-label={`Select all ${getAllOptions().length} options`}
                      className='cursor-pointer'
                    >
                      <span>
                        (Select All
                        {getAllOptions().length > 20 ? ` - ${getAllOptions().length} options` : ''})
                      </span>
                      <CheckIcon
                        className={cn(
                          'ml-auto h-4 w-4',
                          selectedValues.length === getAllOptions().filter((opt) => !opt.disabled).length
                            ? 'opacity-100'
                            : 'opacity-0'
                        )}
                        aria-hidden='true'
                      />
                    </CommandItem>
                  </CommandGroup>
                )}
                {isGroupedOptions(filteredOptions) ? (
                  filteredOptions.map((group) => (
                    <CommandGroup key={group.heading} heading={group.heading}>
                      {group.options.map((option) => {
                        const isSelected = selectedValues.includes(option.value);
                        return (
                          <CommandItem
                            key={option.value}
                            onSelect={() => toggleOption(option.value)}
                            role='option'
                            aria-selected={isSelected}
                            aria-disabled={option.disabled}
                            aria-label={`${option.label}${isSelected ? ', selected' : ', not selected'}${
                              option.disabled ? ', disabled' : ''
                            }`}
                            className={cn(
                              'cursor-pointer text-xs rounded-[10px] mb-1.5 last:mb-0 data-[selected=true]:bg-gray-100',
                              isSelected && 'bg-gray-300 data-[selected=true]:bg-gray-300',
                              option.disabled && 'opacity-50 cursor-not-allowed'
                            )}
                            disabled={option.disabled}
                          >
                            {option.icon && <option.icon className='mr-2 h-3 w-3 text-muted-foreground' aria-hidden='true' />}
                            <OptionColorDot color={option.style?.badgeColor} />
                            <span className='truncate'>{option.label}</span>
                            {option.badge && (
                              <span
                                className={cn(
                                  'ml-auto mr-2 shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium',
                                  option.badge.className
                                )}
                              >
                                {option.badge.label}
                              </span>
                            )}
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  ))
                ) : (
                  <CommandGroup>
                    {filteredOptions.map((option) => {
                      const isSelected = selectedValues.includes(option.value);
                      return (
                        <CommandItem
                          key={option.value}
                          onSelect={() => toggleOption(option.value)}
                          role='option'
                          aria-selected={isSelected}
                          aria-disabled={option.disabled}
                          aria-label={`${option.label}${isSelected ? ', selected' : ', not selected'}${
                            option.disabled ? ', disabled' : ''
                          }`}
                          className={cn(
                            'cursor-pointer text-xs rounded-[10px] mb-1.5 last:mb-0 data-[selected=true]:bg-gray-100',
                            isSelected && 'bg-gray-300 data-[selected=true]:bg-gray-300',
                            option.disabled && 'opacity-50 cursor-not-allowed'
                          )}
                          disabled={option.disabled}
                        >
                          {option.icon && <option.icon className='mr-2 h-3 w-3 text-muted-foreground' aria-hidden='true' />}
                          <OptionColorDot color={option.style?.badgeColor} />
                          <span className='truncate'>{option.label}</span>
                          {option.badge && (
                            <span
                              className={cn(
                                'ml-auto mr-2 shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium',
                                option.badge.className
                              )}
                            >
                              {option.badge.label}
                            </span>
                          )}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
          {animation > 0 && selectedValues.length > 0 && (
            <WandSparkles
              className={cn(
                'cursor-pointer my-2 text-foreground bg-background w-3 h-3',
                isAnimating ? '' : 'text-muted-foreground'
              )}
              onClick={() => setIsAnimating(!isAnimating)}
            />
          )}
        </Popover>
      </>
    );
  }
);

MultiSelect.displayName = 'MultiSelect';
export type { MultiSelectOption, MultiSelectGroup, MultiSelectProps };
